import type React from "react";
import { useEffect, useState, useCallback, useRef } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useToast, ToastType, Spinner } from "@cynaps/ui";
import { AnnotationTestTask, TestResultsDisplay } from "./components";
import type { TestCase, AnnotationResult } from "./data/testCases";
import type { TestResult } from "./data/scoring";
import "./AnnotatorSkillTest.css"; // Reuse the same styles

type TestPhase = "loading" | "instructions" | "testing" | "results" | "error";

interface ExpertiseInfo {
  id: number;
  category_name: string;
  category_slug: string;
  specialization_name: string | null;
  specialization_slug: string | null;
  annotator_name: string;
  test_attempts: number;
}

/** API task data returned from the backend POST /test-by-token */
interface APITestTask {
  task_id: number;
  project_title: string;
  label_config: string;
  data: Record<string, any>;
  difficulty: "easy" | "medium" | "hard";
  points: number;
}

/** Convert backend task to TestCase format used by AnnotationTestTask component */
function apiTaskToTestCase(apiTask: APITestTask, index: number): TestCase {
  return {
    id: String(apiTask.task_id),
    specialty: "test",
    title: apiTask.project_title,
    description: `Annotation task ${index + 1}: ${apiTask.project_title}`,
    difficulty: apiTask.difficulty,
    points: apiTask.points,
    config: apiTask.label_config,
    task: {
      data: apiTask.data,
    },
    // Ground truth is kept server-side - not sent to frontend
    groundTruth: [],
    scoringCriteria: { type: "exact" },
  };
}

export const ExpertiseTest: React.FC & { title?: string; path?: string; exact?: boolean } = () => {
  const history = useHistory();
  const location = useLocation();
  const toast = useToast();

  // Get token from URL
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get("token");

  // Test state
  const [phase, setPhase] = useState<TestPhase>("loading");
  const [expertiseInfo, setExpertiseInfo] = useState<ExpertiseInfo | null>(null);
  const [passingScore, setPassingScore] = useState(70);
  const [testCases, setTestCases] = useState<TestCase[]>([]);
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
  const answersRef = useRef<Map<string, AnnotationResult[]>>(new Map());
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [taskCount, setTaskCount] = useState(0);
  const testIdRef = useRef<number | null>(null);

  // Timer
  const [timeRemaining, setTimeRemaining] = useState(0);
  const startTimeRef = useRef<number>(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Validate token and load expertise info
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setErrorMessage("No test token provided. Please use the link from your email.");
        setPhase("error");
        return;
      }

      try {
        const response = await fetch(`/api/annotators/expertise/test-by-token?token=${token}`, {
          credentials: "include",
        });

        const data = await response.json();

        if (!response.ok) {
          setErrorMessage(data.error || "Invalid or expired test link");
          setPhase("error");
          return;
        }

        if (data.status === "already_verified") {
          setErrorMessage("You already have this expertise verified!");
          setPhase("error");
          return;
        }

        setExpertiseInfo(data.expertise);
        setTaskCount(data.questions_available || 0);

        // Set passing score from API
        if (data.passing_score) {
          setPassingScore(data.passing_score);
        }

        setPhase("instructions");
      } catch (error) {
        console.error("Token validation failed:", error);
        setErrorMessage("Failed to validate test link. Please try again.");
        setPhase("error");
      }
    };

    validateToken();
  }, [token]);

  // Timer effect
  useEffect(() => {
    if (phase === "testing" && timeRemaining > 0) {
      timerRef.current = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            handleFinishTest();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => {
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
      };
    }
  }, [phase, timeRemaining]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // Start the test — POST to backend to get real annotation tasks
  const handleStartTest = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);

    try {
      const response = await fetch("/api/annotators/expertise/test-by-token", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });

      const data = await response.json();

      if (!response.ok) {
        setErrorMessage(data.error || "Failed to start test");
        setPhase("error");
        return;
      }

      // Store test ID for submission
      testIdRef.current = data.test_id;

      // Convert API tasks to TestCase format
      const apiTasks: APITestTask[] = data.tasks || [];
      if (apiTasks.length === 0) {
        setErrorMessage("No test tasks available. Please contact support.");
        setPhase("error");
        return;
      }

      const cases = apiTasks.map((t, i) => apiTaskToTestCase(t, i));
      setTestCases(cases);

      // Start timer (use time limit from API or 30 min default)
      const timeLimitMinutes = data.time_limit_minutes || 30;
      setTimeRemaining(timeLimitMinutes * 60);
      startTimeRef.current = Date.now();
      setPhase("testing");
    } catch (error) {
      console.error("Failed to start test:", error);
      setErrorMessage("Failed to start test. Please try again.");
      setPhase("error");
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  // Handle task completion
  const handleTaskComplete = useCallback(
    (result: AnnotationResult[]) => {
      const currentTask = testCases[currentTaskIndex];
      answersRef.current.set(currentTask.id, result);

      if (currentTaskIndex < testCases.length - 1) {
        setCurrentTaskIndex((prev) => prev + 1);
      } else {
        handleFinishTest();
      }
    },
    [currentTaskIndex, testCases],
  );

  // Finish the test and submit to backend for server-side grading
  const handleFinishTest = useCallback(async () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    setIsLoading(true);

    try {
      const timeTaken = Math.floor((Date.now() - startTimeRef.current) / 1000);

      // Build answers map: task_id → annotation results
      const answersPayload: Record<string, AnnotationResult[]> = {};
      for (const [taskId, annotations] of answersRef.current.entries()) {
        answersPayload[taskId] = annotations;
      }

      // Submit to backend — server does the grading
      const response = await fetch("/api/annotators/expertise/test/submit-by-token", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token,
          test_id: testIdRef.current,
          answers: answersPayload,
          time_taken: timeTaken,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        toast?.show({
          message: data.error || "Failed to submit test",
          type: ToastType.error,
          duration: 5000,
        });
        setPhase("error");
        setErrorMessage(data.error || "Failed to submit test results");
        return;
      }

      if (data.badge_earned) {
        toast?.show({
          message: `Congratulations! You earned the ${data.expertise_name} badge!`,
          type: ToastType.info,
          duration: 8000,
        });
      }

      // Build TestResult from server response for the results display
      const taskScores = (data.task_scores || []).map((ts: any) => ({
        testCaseId: String(ts.task_id),
        specialty: expertiseInfo?.category_slug || "test",
        title: ts.project_title || "Task",
        maxPoints: ts.points || 10,
        earnedPoints: ts.earned || 0,
        percentage: ts.percentage || 0,
        feedback: ts.has_ground_truth ? (ts.percentage >= 70 ? "Good work!" : "Needs improvement") : "Completed",
        details: [],
      }));

      const specialtyKey = expertiseInfo?.category_slug || "test";
      const result: TestResult = {
        totalPoints: data.total_points || 0,
        earnedPoints: data.earned_points || 0,
        percentage: data.score || 0,
        passed: data.passed || false,
        passingThreshold: data.passing_score || 70,
        taskScores,
        specialtyScores: {
          [specialtyKey]: {
            specialty: specialtyKey,
            totalPoints: data.total_points || 0,
            earnedPoints: data.earned_points || 0,
            percentage: data.score || 0,
            taskCount: taskScores.length,
            passed: data.passed || false,
          },
        },
        timeTaken,
      };

      setTestResult(result);
      setPhase("results");
    } catch (error) {
      console.error("Failed to submit test:", error);
      toast?.show({
        message: "Failed to submit test results",
        type: ToastType.error,
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  }, [testCases, token, toast, expertiseInfo]);

  // Skip to next task
  const handleSkipTask = useCallback(() => {
    const currentTask = testCases[currentTaskIndex];
    answersRef.current.set(currentTask.id, []);

    if (currentTaskIndex < testCases.length - 1) {
      setCurrentTaskIndex((prev) => prev + 1);
    } else {
      handleFinishTest();
    }
  }, [currentTaskIndex, testCases, handleFinishTest]);

  // Handle retry
  const handleRetry = useCallback(() => {
    setCurrentTaskIndex(0);
    answersRef.current.clear();
    setTestResult(null);
    testIdRef.current = null;
    setPhase("instructions");
  }, []);

  // Handle continue - redirect to dashboard
  const handleContinue = useCallback(() => {
    window.location.href = "/login/?next=/annotator/earnings";
  }, []);

  // Render based on phase
  if (phase === "loading") {
    return (
      <div className="annotator-skill-test">
        <div className="test-instructions">
          <div className="test-instructions__content" style={{ textAlign: "center" }}>
            <Spinner size={48} />
            <h2 style={{ color: "#fff", marginTop: "1rem" }}>Validating test link...</h2>
          </div>
        </div>
      </div>
    );
  }

  if (phase === "error") {
    return (
      <div className="annotator-skill-test">
        <div className="test-instructions">
          <div className="test-instructions__content" style={{ textAlign: "center" }}>
            <div style={{ fontSize: "4rem", marginBottom: "1rem" }}>&#10060;</div>
            <h2 className="test-instructions__title">Unable to Start Test</h2>
            <p style={{ color: "#9ca3af", marginBottom: "2rem" }}>{errorMessage}</p>
            <button className="test-instructions__start" onClick={() => window.close()}>
              Close Window
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (phase === "instructions") {
    const expertiseName = expertiseInfo?.specialization_name || expertiseInfo?.category_name || "Expertise";
    return (
      <div className="annotator-skill-test">
        <div className="test-instructions">
          <div className="test-instructions__content">
            <div className="test-instructions__number">EXPERTISE QUALIFICATION TEST</div>
            <h1 className="test-instructions__title">{expertiseName}</h1>

            <div className="test-instructions__summary">
              <div className="test-instructions__summary-item">
                <span className="test-instructions__summary-label">Tasks</span>
                <span className="test-instructions__summary-value">5–10</span>
              </div>
              <div className="test-instructions__summary-item">
                <span className="test-instructions__summary-label">Time</span>
                <span className="test-instructions__summary-value">30 min</span>
              </div>
              <div className="test-instructions__summary-item">
                <span className="test-instructions__summary-label">Passing</span>
                <span className="test-instructions__summary-value">{passingScore}%</span>
              </div>
            </div>

            <div className="test-instructions__rules">
              <h3>Test Guidelines</h3>
              <ul>
                <li>
                  <span className="test-instructions__rule-icon">&#10003;</span>
                  You will be given real annotation tasks across different templates
                </li>
                <li>
                  <span className="test-instructions__rule-icon">&#10003;</span>
                  Complete each labeling task to the best of your ability
                </li>
                <li>
                  <span className="test-instructions__rule-icon">&#10003;</span>
                  You must score at least {passingScore}% to earn your expertise badge
                </li>
                <li>
                  <span className="test-instructions__rule-icon">&#10003;</span>
                  The timer starts when you click &quot;Start Test&quot;
                </li>
                <li>
                  <span className="test-instructions__rule-icon">&#10003;</span>
                  You can skip tasks but they will count as unanswered
                </li>
              </ul>
            </div>

            <div className="test-instructions__actions">
              <button className="test-instructions__back" onClick={() => window.close()}>
                &larr; Close
              </button>
              <button className="test-instructions__start" onClick={handleStartTest} disabled={isLoading}>
                {isLoading ? "Loading tasks..." : "Start Test \u2192"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (phase === "testing") {
    const currentTestCase = testCases[currentTaskIndex];
    return (
      <div className="annotator-skill-test">
        <AnnotationTestTask
          key={currentTestCase.id}
          testCase={currentTestCase}
          onComplete={handleTaskComplete}
          onSkip={handleSkipTask}
          taskNumber={currentTaskIndex + 1}
          totalTasks={testCases.length}
          timeRemaining={timeRemaining}
        />
      </div>
    );
  }

  if (phase === "results" && testResult) {
    return (
      <div className="annotator-skill-test">
        <TestResultsDisplay result={testResult} onRetry={handleRetry} onContinue={handleContinue} />
      </div>
    );
  }

  return null;
};
